import React, { useEffect } from 'react';
import { CheckCircle, XCircle, AlertCircle, Info, X } from 'lucide-react';

const Toast = ({ message, type = 'success', onClose, duration = 4000 }) => {
  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(onClose, duration);
      return () => clearTimeout(timer);
    }
  }, [duration, onClose]);

  const config = {
    success: {
      icon: CheckCircle,
      bgGradient: 'from-green-500 to-emerald-600',
      iconColor: 'text-green-100',
      borderColor: 'border-green-400'
    },
    error: {
      icon: XCircle,
      bgGradient: 'from-red-500 to-rose-600',
      iconColor: 'text-red-100',
      borderColor: 'border-red-400'
    },
    warning: {
      icon: AlertCircle,
      bgGradient: 'from-yellow-500 to-orange-600',
      iconColor: 'text-yellow-100',
      borderColor: 'border-yellow-400'
    },
    info: {
      icon: Info,
      bgGradient: 'from-blue-500 to-cyan-600',
      iconColor: 'text-blue-100',
      borderColor: 'border-blue-400'
    }
  };

  const { icon: Icon, bgGradient, iconColor, borderColor } = config[type];

  return (
    <div 
      className={`fixed top-4 right-4 z-[9999] animate-slide-in-right`}
      style={{
        animation: 'slideInRight 0.3s ease-out, fadeOut 0.3s ease-out ' + (duration - 300) + 'ms forwards'
      }}
    >
      <div className={`bg-gradient-to-r ${bgGradient} text-white rounded-xl shadow-2xl border-2 ${borderColor} p-4 pr-12 min-w-[300px] max-w-md backdrop-blur-sm`}>
        <div className="flex items-start gap-3">
          <Icon className={`${iconColor} flex-shrink-0 mt-0.5`} size={24} />
          <p className="text-sm font-medium leading-relaxed">{message}</p>
          <button
            onClick={onClose}
            className="absolute top-2 right-2 p-1 hover:bg-white/20 rounded-lg transition-colors"
            aria-label="Fermer"
          >
            <X size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Toast;

// Hook personnalisé pour utiliser les toasts
export const useToast = () => {
  const [toasts, setToasts] = React.useState([]);

  const showToast = React.useCallback((message, type = 'success', duration = 4000) => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type, duration }]);
  }, []);

  const removeToast = React.useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const ToastContainer = React.useCallback(() => (
    <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-3">
      {toasts.map(toast => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          duration={toast.duration}
          onClose={() => removeToast(toast.id)}
        />
      ))}
    </div>
  ), [toasts, removeToast]);

  return { showToast, ToastContainer };
};
